<script setup lang="ts">
import { computed } from "vue";

import { browserIcon, osIcon, deviceIcon, type ParsedUserAgent } from "../../utils/userAgent";
import Tooltip from "./Tooltip.vue";

// Renders the server-parsed user agent as browser/OS/(device) icons plus a
// summary label, falling back to the raw string when the backend couldn't parse
// it. A hover tooltip shows the full raw user-agent string so it stays
// inspectable (mirrors the IP address hover).
const props = defineProps<{
  parsed?: ParsedUserAgent | null;
  raw?: string;
  // When true, render only the browser/OS/device icons (no summary label). The
  // full user agent stays available via the hover tooltip. Used by the
  // top-level session row; the metadata timeline keeps the labelled view.
  iconsOnly?: boolean;
}>();

const hasParsed = computed(() => Boolean(props.parsed && props.parsed.Summary));
const fullUserAgent = computed(() => props.raw || props.parsed?.Summary || "");
// Always show the device glyph (desktop/mobile/tablet/...) when known, so the
// device type reads at a glance in both the icons-only row and the labelled
// timeline view.
const showDevice = computed(() => Boolean(props.parsed?.DeviceType));
</script>

<template>
  <Tooltip :text="fullUserAgent" side="top" focusable>
    <span v-if="hasParsed" class="inline-flex items-center gap-1.5">
      <font-awesome-icon :icon="browserIcon(parsed!.Browser)" class="h-3.5 w-3.5 text-muted-foreground" />
      <font-awesome-icon :icon="osIcon(parsed!.OS)" class="h-3.5 w-3.5 text-muted-foreground" />
      <font-awesome-icon
        v-if="showDevice"
        :icon="deviceIcon(parsed!.DeviceType)"
        class="h-3.5 w-3.5 text-muted-foreground"
      />
      <span v-if="!iconsOnly" class="truncate">{{ parsed!.Summary }}</span>
    </span>
    <span v-else class="truncate">{{ raw || "-" }}</span>

    <template #content>
      <div class="space-y-1">
        <div v-if="hasParsed" class="font-medium text-foreground">{{ parsed!.Summary }}</div>
        <div class="max-w-xs whitespace-normal break-all font-mono text-[11px] text-muted-foreground">
          {{ fullUserAgent }}
        </div>
      </div>
    </template>
  </Tooltip>
</template>
